import {flatten} from 'flat'
import {FormikErrors, useFormikContext} from 'formik'
import {FC, useEffect, useRef} from 'react'

import {getScrollBehavior} from '@/utils/ui/scrollBehavior'

export function scrollToFirstError(
  errors: FormikErrors<unknown>,
  config?: {
    delay?: number
    scrollOptions?: ScrollIntoViewOptions
  },
) {
  const {delay = 200, scrollOptions = {behavior: getScrollBehavior(), block: 'center'}} =
    config || {}

  const errorKeys = Object.keys(flatten(errors))

  if (!errorKeys.length) return null

  const errorElements = errorKeys
    .map((key) => {
      const element = (document.querySelector(`[name="${key}"]`) ??
        document.querySelector(`input[id="${key}"]`) ??
        document.querySelector(`[data-test="${key}"]`)) as HTMLElement | null
      const elementTop = element?.getBoundingClientRect()?.top
      const offset = elementTop ? elementTop + window.scrollY : undefined

      return {element, offset}
    })
    .filter(({element, offset}) => Boolean(element && offset))
    .sort((a, b) => a.offset! - b.offset!)

  if (!errorElements.length) return null

  return setTimeout(() => {
    const element = errorElements[0].element as HTMLElement
    element.scrollIntoView(scrollOptions)
    if (element.tagName === 'DIV') {
      window.addEventListener('scrollend', () => element.click(), {once: true})
    } else {
      element.focus({preventScroll: true})
    }
  }, delay)
}

type FormErrorScrollProps = {
  delay?: number
  scrollOptions?: ScrollIntoViewOptions
}

export const FormErrorScroll: FC<FormErrorScrollProps> = ({delay = 200, scrollOptions}) => {
  const {errors, isSubmitting, isValidating, submitCount} = useFormikContext()
  const timer = useRef<NodeJS.Timeout | null>(null)

  const scrollToError = () => {
    if (isSubmitting && !isValidating) {
      timer.current = scrollToFirstError(errors, {delay, scrollOptions})
    }

    return () => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(scrollToError, [submitCount, isValidating])

  return null
}
